// =====================================================================
// myCRM — Sales Dashboard (unified GTM)
// =====================================================================

function SalesDashboard({ navigate }) {
  const { persona } = useContext(PersonaContext);

  const [filterRep, setFilterRep] = useState("all");
  const [filterSegment, setFilterSegment] = useState("all");
  const [filterPeriod, setFilterPeriod] = useState("This quarter");
  const [tab, setTab] = useState("charts");

  const opps = useMemo(() => D.OPPORTUNITIES.filter(o => {
    const acct = getAccount(o.accountId);
    if (filterRep !== "all" && o.owner !== filterRep) return false;
    if (filterSegment !== "all" && acct.segment !== filterSegment) return false;
    return !o.stage.startsWith("Closed");
  }), [filterRep, filterSegment]);

  const totalACV = opps.reduce((a, o) => a + o.acv, 0);
  const commitACV = opps.filter(o => o.forecastCategory === "Commit").reduce((a, o) => a + o.acv, 0);
  const bestACV = opps.filter(o => o.forecastCategory === "Best case").reduce((a, o) => a + o.acv, 0);
  const pipeACV = opps.filter(o => o.forecastCategory === "Pipeline").reduce((a, o) => a + o.acv, 0);
  const totalQuota = assignableReps().reduce((a, u) => a + (u.quota || 0), 0);

  const stageCounts = D.STAGES.map(s => ({ stage: s, count: opps.filter(o => o.stage === s).length, acv: opps.filter(o => o.stage === s).reduce((a, o) => a + o.acv, 0) })).filter(s => s.count > 0);
  const meddDist = [0,1,2,3,4,5,6,7,8,9].map(n => opps.filter(o => meddScore(o).got === n).length);

  // Tasks come from real Task-type activities (logged via "Log activity").
  const today = new Date().toISOString().slice(0, 10);
  const taskActivities = (D.ACTIVITIES || []).filter(a => a.type === "Task");
  const overdue = taskActivities
    .filter(a => a.date && a.date < today)
    .sort((a, b) => a.date.localeCompare(b.date));
  const tasksToday = taskActivities
    .filter(a => !a.date || a.date >= today)
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const taskLink = (t) => t.oppId ? "sales/opportunities/" + t.oppId
    : t.contactId ? "sales/contacts/" + t.contactId
    : t.accountId ? "sales/accounts/" + t.accountId : null;

  const recentResearch = D.SURVEYS.filter(s => s.status === "Completed").slice(0, 4).concat(
    D.INTERVIEWS.filter(i => i.status === "Completed").slice(0, 2).map(i => ({ ...i, isInterview: true }))
  ).sort((a,b) => new Date(b.completed || b.date) - new Date(a.completed || a.date)).slice(0, 5);

  // Persona-driven panel visibility
  const show = useMemo(() => {
    switch (persona) {
      case "AE":       return { pipeline: true,  medd: true,  forecast: true,  tasks: true,  overdue: true,  research: true,  sequences: false, quota: true  };
      case "Manager":  return { pipeline: true,  medd: false, forecast: true,  tasks: true,  overdue: true,  research: false, sequences: false, quota: true  };
      case "SDR":      return { pipeline: false, medd: false, forecast: false, tasks: true,  overdue: true,  research: false, sequences: true,  quota: true  };
      case "Research": return { pipeline: false, medd: false, forecast: false, tasks: false, overdue: false, research: true,  sequences: false, quota: false };
      default:         return { pipeline: true,  medd: true,  forecast: true,  tasks: true,  overdue: true,  research: true,  sequences: true,  quota: true  };
    }
  }, [persona]);

  // Persona-specific KPIs
  const kpis = useMemo(() => {
    if (persona === "SDR") {
      const agg = D.SEQUENCES.reduce((acc, s) => {
        const st = seqStats(s.id);
        acc.enrolled += st.enrolled; acc.replied += st.replied; acc.meetings += st.meetings;
        return acc;
      }, { enrolled: 0, replied: 0, meetings: 0 });
      return [
        { label: "Total enrolled",   value: agg.enrolled },
        { label: "Reply rate",       value: (agg.enrolled ? Math.round(agg.replied / agg.enrolled * 100) : 0) + "%" },
        { label: "Meeting rate",     value: (agg.enrolled ? Math.round(agg.meetings / agg.enrolled * 100) : 0) + "%" },
        { label: "Active sequences", value: D.SEQUENCES.length },
      ];
    }
    if (persona === "Research") {
      const survDone = D.SURVEYS.filter(s => s.status === "Completed").length;
      const intDone = D.INTERVIEWS.filter(i => i.status === "Completed").length;
      const npsScores = D.SURVEYS.filter(s => s.nps != null).map(s => s.nps);
      const avgNps = npsScores.length
        ? Math.round(npsScores.reduce((a, n) => a + n, 0) / npsScores.length)
        : null;
      const researchContacts = new Set([
        ...D.SURVEYS.map(s => s.contactId),
        ...D.INTERVIEWS.map(i => i.contactId),
      ]).size;
      return [
        { label: "Surveys completed",  value: survDone },
        { label: "Interviews done",    value: intDone },
        { label: "Avg NPS",            value: avgNps != null ? avgNps : "—" },
        { label: "Research contacts",  value: researchContacts },
      ];
    }
    // GTM / AE / Manager
    return [
      { label: "Open pipeline (ACV)",  value: fmtMoney(totalACV) },
      { label: "Commit (ACV)",         value: fmtMoney(commitACV),  delta: totalQuota ? Math.round(commitACV / totalQuota * 100) + "% to quota" : null },
      { label: "Best case (ACV)",      value: fmtMoney(bestACV) },
      { label: "Open opportunities",   value: opps.length,          delta: opps.length ? `avg ${Math.round(opps.reduce((a,o)=>a+o.daysInStage,0)/opps.length)}d in stage` : "no open deals" },
    ];
  }, [persona, totalACV, commitACV, bestACV, opps, totalQuota, D.SEQUENCES, D.ENROLLMENTS]);

  // Tab split — visual cards (bars/distributions/quota) vs text-style
  // panels (lists & tables). The KPI row and filters stay above the
  // tabs since they're shared context for both views.
  const chartsRowCount = [show.pipeline, show.medd, show.forecast].filter(Boolean).length;
  const quotaRowVisible = show.quota;
  const listsRowCount = [show.tasks, show.overdue, show.research].filter(Boolean).length;
  const sequencesRowVisible = show.sequences;

  const chartsHasContent = chartsRowCount > 0 || quotaRowVisible;
  const listsHasContent  = listsRowCount > 0 || sequencesRowVisible;

  const tabs = [];
  if (chartsHasContent) tabs.push({ key: "charts", label: "Charts" });
  if (listsHasContent)  tabs.push({ key: "lists",  label: "Lists"  });

  // If the active tab has no content for this persona, fall back to
  // whichever tab does. (Research persona, for example, has no charts.)
  const activeTab = tabs.some(t => t.key === tab) ? tab : (tabs[0] && tabs[0].key);

  return (
    <>
      <PageHead
        title={persona === "GTM" ? "GTM Dashboard" : `${persona} Dashboard`}
        sub={`Unified view of pipeline, outreach, and research signals · ${filterPeriod}`}
        actions={
          <>
            <select className="btn btn-sm" value={filterRep} onChange={e => setFilterRep(e.target.value)}>
              <option value="all">All reps</option>
              {assignableReps(["AE", "SDR", "BDR"]).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <select className="btn btn-sm" value={filterSegment} onChange={e => setFilterSegment(e.target.value)}>
              <option value="all">All segments</option>
              <option>SME</option><option>Mid-market</option><option>Enterprise</option>
            </select>
            <select className="btn btn-sm" value={filterPeriod} onChange={e => setFilterPeriod(e.target.value)}>
              <option>This quarter</option><option>This month</option><option>Last 30 days</option><option>Year to date</option>
            </select>
          </>
        }
      />

      <div className="grid grid-4 mt-8">
        {kpis.map((kpi, i) => (
          <Stat key={i} label={kpi.label} value={kpi.value} delta={kpi.delta} deltaDir={kpi.deltaDir} />
        ))}
      </div>

      {tabs.length > 1 && (
        <div style={{ marginTop: 16 }}>
          <Tabs tabs={tabs} current={activeTab} onChange={setTab} />
        </div>
      )}

      {activeTab === "charts" && chartsRowCount > 0 && (
        <div className={`grid grid-${chartsRowCount} mt-16`}
             style={chartsRowCount === 3 ? { gridTemplateColumns: "1.5fr 1fr 1fr" } : {}}>
          {show.pipeline && (
            <div className="card">
              <div className="card-head">
                <div className="card-title">Pipeline by stage</div>
                <div className="card-sub">{opps.length} open · {fmtMoney(totalACV)} ACV</div>
              </div>
              {stageCounts.length === 0 ? (
                <div className="card-body muted">No open opportunities.</div>
              ) : (
                <div className="card-body">
                  {stageCounts.map(s => (
                    <BarRow key={s.stage} label={s.stage} value={s.count} max={Math.max(...stageCounts.map(x => x.count))} />
                  ))}
                </div>
              )}
              <div className="card-foot flex-between">
                <span>Pipeline coverage: <b>{commitACV > 0 ? (totalACV / commitACV).toFixed(1) + "×" : "—"}</b> commit</span>
                <a className="btn btn-sm btn-ghost" onClick={() => navigate("sales/opportunities")}>Open kanban →</a>
              </div>
            </div>
          )}

          {show.medd && (
            <div className="card">
              <div className="card-head">
                <div className="card-title">MEDDPICCR score distribution</div>
                <div className="card-sub">across {opps.length} open deals</div>
              </div>
              <div className="card-body">
                {meddDist.map((n, i) => (
                  <BarRow key={i} label={`${i} / 9`} value={n} max={Math.max(...meddDist)} />
                ))}
                <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
                  <b>{opps.filter(o => meddScore(o).got >= window.tpThresholds.meddpiccForecastThresholds(D.SETTINGS).commit).length}</b> deals meet Commit threshold (≥{window.tpThresholds.meddpiccForecastThresholds(D.SETTINGS).commit} elements).
                </div>
              </div>
            </div>
          )}

          {show.forecast && (
            <div className="card">
              <div className="card-head"><div className="card-title">Forecast mix</div></div>
              <div className="card-body">
                <BarRow label="Commit"    value={Math.round(commitACV/1000)} max={Math.max(commitACV, bestACV, pipeACV)/1000} tone="bar-green" />
                <BarRow label="Best case" value={Math.round(bestACV/1000)}   max={Math.max(commitACV, bestACV, pipeACV)/1000} tone="" />
                <BarRow label="Pipeline"  value={Math.round(pipeACV/1000)}   max={Math.max(commitACV, bestACV, pipeACV)/1000} tone="bar-slate" />
                <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
                  Quota ($k): <b>{Math.round(commitACV/1000)}k</b> committed · {Math.round((bestACV + commitACV)/1000)}k best-case
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "lists" && listsRowCount > 0 && (
        <div className={`grid grid-${listsRowCount} mt-16`}
             style={listsRowCount === 3 ? { gridTemplateColumns: "1fr 1fr 1.2fr" } : {}}>
          {show.tasks && (
            <div className="card">
              <div className="card-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div>
                  <div className="card-title">Today's tasks</div>
                  <div className="card-sub">{tasksToday.length} open</div>
                </div>
                <a className="btn btn-sm btn-ghost" onClick={() => navigate("sales/calendar")} title="See the full fortnight on the Calendar">📅 Calendar</a>
              </div>
              {tasksToday.length === 0 ? (
                <div className="card-body muted">No open tasks. Log a task from any account, contact or opportunity.</div>
              ) : (
                <div className="card-body" style={{ padding: 0 }}>
                  {tasksToday.map(t => {
                    const link = taskLink(t);
                    return (
                      <div key={t.id} className="flex-between" style={{ padding: "10px 16px", borderBottom: "1px solid var(--divider)" }}
                           onClick={() => link && navigate(link)}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{t.subject}</div>
                          <div className="muted" style={{ fontSize: 11.5 }}>{t.date ? "Due " + fmtDate(t.date) : "No due date"}</div>
                        </div>
                        {t.priority && <Badge tone={t.priority === "High" ? "red" : t.priority === "Low" ? "slate" : "amber"}>{t.priority}</Badge>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {show.overdue && (
            <div className="card">
              <div className="card-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div>
                  <div className="card-title">Overdue</div>
                  <div className="card-sub">{overdue.length} need attention</div>
                </div>
                <a className="btn btn-sm btn-ghost" onClick={() => navigate("sales/activity")} title="See the full follow-ups queue">↗ Follow-ups</a>
              </div>
              {overdue.length === 0 ? (
                <div className="card-body muted">Nothing overdue.</div>
              ) : (
                <div className="card-body" style={{ padding: 0 }}>
                  {overdue.map(t => {
                    const link = taskLink(t);
                    return (
                      <div key={t.id} className="flex-between" style={{ padding: "10px 16px", borderBottom: "1px solid var(--divider)" }}
                           onClick={() => link && navigate(link)}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{t.subject}</div>
                          <div className="muted" style={{ fontSize: 11.5 }}>Due {fmtDate(t.date)}</div>
                        </div>
                        <Badge tone="red">Overdue</Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {show.research && (
            <div className="card">
              <div className="card-head">
                <div className="card-title">Recent research signals</div>
                <div className="card-sub">From surveys & interviews · embedded in deal context</div>
              </div>
              {recentResearch.length === 0 ? (
                <div className="card-body muted">No research recorded yet.</div>
              ) : (
              <div className="card-body" style={{ padding: 0 }}>
                {recentResearch.map(r => {
                  const c = getContact(r.contactId) || {};
                  const a = getAccount(r.accountId) || {};
                  return (
                    <div key={r.id} style={{ padding: "10px 16px", borderBottom: "1px solid var(--divider)" }}
                         onClick={() => navigate(`research/${r.isInterview ? "interviews" : "surveys"}/${r.id}`)}>
                      <div className="flex-between">
                        <div style={{ fontSize: 12.5, fontWeight: 500 }}>{r.type} — {a.name}</div>
                        <Badge tone="amber">{r.isInterview ? "Interview" : "Survey"}</Badge>
                      </div>
                      <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                        {c.first} {c.last} · {fmtDate(r.completed || r.date)}
                      </div>
                      {r.keyQuote && <div style={{ fontSize: 12.5, fontStyle: "italic", marginTop: 4, color: "var(--text-muted)" }}>"{r.keyQuote}"</div>}
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === "charts" && quotaRowVisible && (
        <div className="grid grid-1 mt-16">
          <div className="card">
            <div className="card-head"><div className="card-title">Quota attainment</div><div className="card-sub">current quarter</div></div>
            {assignableReps(["AE", "SDR", "BDR"]).length === 0 ? (
              <div className="card-body muted">No sales reps yet.</div>
            ) : (
              <div className="card-body">
                {assignableReps(["AE", "SDR", "BDR"]).map(u => (
                  <div key={u.id} className="flex-between" style={{ padding: "8px 0", borderBottom: "1px solid var(--divider)" }}>
                    <div className="flex-gap" style={{ alignItems: "center" }}>
                      <Avatar user={u} size={28} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{u.name}</div>
                        <div className="muted" style={{ fontSize: 11.5 }}>{u.role} · {u.team}</div>
                      </div>
                    </div>
                    <div style={{ minWidth: 180 }}>
                      <div className="bar-track"><div className="bar-fill" style={{ width: `${Math.min(100, u.attainment*100)}%`, background: u.attainment >= 1 ? "var(--green)" : u.attainment >= 0.7 ? "var(--accent)" : "var(--red)" }} /></div>
                      <div className="muted right" style={{ fontSize: 11.5, marginTop: 2 }}>{Math.round(u.attainment*100)}% of quota</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "lists" && sequencesRowVisible && (
        <div className="grid grid-1 mt-16">
          <div className="card">
            <div className="card-head"><div className="card-title">Sequence performance</div><div className="card-sub">reply rate · meetings booked</div></div>
            {D.SEQUENCES.length === 0 ? (
              <div className="card-body muted">No sequences yet.</div>
            ) : (
              <div className="card-body" style={{ padding: 0 }}>
                <table className="tbl tbl-flush">
                  <thead><tr><th>Sequence</th><th>Segment</th><th className="right">Enrolled</th><th className="right">Reply</th><th className="right">Mtg booked</th></tr></thead>
                  <tbody>
                    {D.SEQUENCES.map(s => {
                      const st = seqStats(s.id);
                      return (
                      <tr key={s.id} onClick={() => navigate("sales/sequences/" + s.id)}>
                        <td><span className="tbl-link">{s.name}</span></td>
                        <td><Badge tone="slate">{s.segment}</Badge></td>
                        <td className="right numeric">{st.enrolled}</td>
                        <td className="right numeric">{Math.round(st.replyRate*100)}%</td>
                        <td className="right numeric">{Math.round(st.meetingRate*100)}%</td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

window.SalesDashboard = SalesDashboard;
